import resourcesToBackend from 'i18next-resources-to-backend';

export const WIDGET_I18N_NAMESPACES = {
  heatmapWidget: 'heatmapWidget',
  alertsManagerWidget: 'alertsManagerWidget',
  alertsRecommendations: 'alertsRecommendations',
  videoBroadcastWidget: 'videoBroadcastWidget',
  directionalDrillingWidget: 'directionalDrillingWidget',
} as const;

export type WidgetI18nNamespace = (typeof WIDGET_I18N_NAMESPACES)[keyof typeof WIDGET_I18N_NAMESPACES];

const SUPPORTED_WIDGET_LOCALES = ['en', 'ru', 'es'] as const;
type WidgetLocale = (typeof SUPPORTED_WIDGET_LOCALES)[number];
const DEFAULT_WIDGET_LOCALE: WidgetLocale = 'en';

const normalizeWidgetLocale = (lang: string): WidgetLocale => {
  const locale = lang.split('-')[0];
  if (SUPPORTED_WIDGET_LOCALES.includes(locale as WidgetLocale)) return locale as WidgetLocale;
  return DEFAULT_WIDGET_LOCALE;
};

const WIDGET_NAMESPACE_LOADERS: Record<
  WidgetI18nNamespace,
  Record<WidgetLocale, () => Promise<Record<string, unknown>>>
> = {
  [WIDGET_I18N_NAMESPACES.heatmapWidget]: {
    en: async () => (await import('@repo/go-widgets/locale/heatmap-widget/en')).default,
    ru: async () => (await import('@repo/go-widgets/locale/heatmap-widget/ru')).default,
    es: async () => (await import('@repo/go-widgets/locale/heatmap-widget/es')).default,
  },
  [WIDGET_I18N_NAMESPACES.alertsManagerWidget]: {
    en: async () => (await import('@repo/go-widgets/locale/en')).default,
    ru: async () => (await import('@repo/go-widgets/locale/ru')).default,
    es: async () => (await import('@repo/go-widgets/locale/es')).default,
  },
  [WIDGET_I18N_NAMESPACES.alertsRecommendations]: {
    en: async () => (await import('@repo/go-widgets/locale/recommendations/en')).default,
    ru: async () => (await import('@repo/go-widgets/locale/recommendations/ru')).default,
    es: async () => (await import('@repo/go-widgets/locale/recommendations/es')).default,
  },
  [WIDGET_I18N_NAMESPACES.directionalDrillingWidget]: {
    en: async () => (await import('@repo/go-widgets/locale/directional-drilling-widget/en')).default,
    ru: async () => (await import('@repo/go-widgets/locale/directional-drilling-widget/ru')).default,
    es: async () => (await import('@repo/go-widgets/locale/directional-drilling-widget/es')).default,
  },
  [WIDGET_I18N_NAMESPACES.videoBroadcastWidget]: {
    en: async () => (await import('@repo/go-widgets/locale/video-broadcast-widget/en')).default,
    ru: async () => (await import('@repo/go-widgets/locale/video-broadcast-widget/ru')).default,
    es: async () => (await import('@repo/go-widgets/locale/video-broadcast-widget/es')).default,
  },
};

const isWidgetNamespace = (namespace: string): namespace is WidgetI18nNamespace => {
  return namespace in WIDGET_NAMESPACE_LOADERS;
};

export const widgetNamespacesBackend = resourcesToBackend(async (lang: string, namespace: string) => {
  if (!isWidgetNamespace(namespace)) return {};

  const locale = normalizeWidgetLocale(lang);
  return WIDGET_NAMESPACE_LOADERS[namespace][locale]();
});
